import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import { useDraftStore } from '@/lib/draftStore';
import { useCreateAuction, useUpdateAuction, useAuction } from '@/hooks/useAuctions';
import { haptic } from '@/lib/telegram';

import { useBackNavigation } from '@/hooks/useBackNavigation';

import { Stepper } from '@/components/auction/Stepper';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';

import { Step1Photos, validateStep1 } from '@/components/auction/steps/Step1Photos';
import { Step2Basic, validateStep2 } from '@/components/auction/steps/Step2Basic';
import { Step3Pricing, validateStep3 } from '@/components/auction/steps/Step3Pricing';
import { Step4Tags, validateStep4 } from '@/components/auction/steps/Step4Tags';
import { Step5Preview } from '@/components/auction/steps/Step5Preview';

import styles from './CreateAuctionPage.module.css';

const STEP_LABELS = ['Фото', 'Основное', 'Цены', 'Категории', 'Превью'];
const TOTAL_STEPS = 5;

export const CreateAuctionPage = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = !!editId;

  const [step, setStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<string | null>(null);

  const draft = useDraftStore((s) => s.draft);
  const update = useDraftStore((s) => s.update);
  const reset = useDraftStore((s) => s.reset);

  const create = useCreateAuction();
  const editMutation = useUpdateAuction(editId ?? '');

  const { data: auction, isLoading: auctionLoading } = useAuction(isEdit ? editId : undefined);

  const back = useBackNavigation();

  // ── Гидрация драфта данными лота (в edit-режиме, один раз на лот) ──
  // Сброс + заполнение в одном эффекте, БЕЗ cleanup-reset:
  // cleanup-сброс в связке с StrictMode (двойной монтаж в dev) стирал
  // свежезаполненный драфт. Поэтому reset() зовём здесь же, перед записью.
  const hydratedForId = useRef<string | null>(null);
  useEffect(() => {
    if (!isEdit || !auction) return;
    // Гидрируем заново, если это другой лот (или ещё не гидрировали этот)
    if (hydratedForId.current === auction.id) return;
    hydratedForId.current = auction.id;

    reset(); // очищаем прежнее состояние драфта
    update({
      title: auction.title,
      description: auction.description,
      condition: auction.condition,
      city: auction.city,
      start_price: auction.start_price,
      min_step: auction.min_step,
      buy_now_price: auction.buy_now_price,
      photos: auction.photos.map((p) => ({
        storage_key: p.storage_key,
        url_original: p.url_original,
        url_thumb: p.url_thumb,
        url_medium: p.url_medium,
        width: p.width,
        height: p.height,
      })),
      tags: auction.tags.map((t) => t.slug),
    });
  }, [isEdit, auction, update, reset]);

  const validate = (s: number): string | null => {
    switch (s) {
      case 1: return validateStep1(draft);
      case 2: return validateStep2(draft);
      case 3: return validateStep3(draft);
      case 4: return validateStep4(draft);
      default: return null;
    }
  };

  const goNext = () => {
    const err = validate(step);
    if (err) {
      setError(err);
      haptic.error();
      return;
    }
    setError(null);
    haptic.light();
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  const goBack = () => {
    setError(null);
    haptic.light();
    if (step === 1) {
      back();
    } else {
      setStep((s) => Math.max(1, s - 1));
    }
  };

  const handleSubmit = async () => {
    setError(null);
    const payload = {
      title: draft.title,
      description: draft.description,
      condition: draft.condition,
      city: draft.city,
      photos: draft.photos,
      start_price: draft.start_price,
      min_step: draft.min_step,
      buy_now_price: draft.buy_now_price,
      duration_minutes: draft.duration_minutes,
      tags: draft.tags,
    };

    try {
      if (isEdit) {
        const updated = await editMutation.mutateAsync(payload);
        haptic.success();
        reset();
        setSubmitted(updated.id);
      } else {
        const auction = await create.mutateAsync(payload);
        haptic.success();
        reset();
        setSubmitted(auction.id);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Не удалось сохранить аукцион';
      setError(msg);
      haptic.error();
    }
  };

  if (isEdit && auctionLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.content}>
          <Skeleton width="100%" height={200} />
          <Skeleton width="80%" height={28} />
          <Skeleton width="60%" height={20} />
        </div>
      </div>
    );
  }

  const isPending = isEdit ? editMutation.isPending : create.isPending;

  if (submitted) {
    return (
      <div className={styles.successPage}>
        <div className={styles.successIcon}>
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 12 5 5L20 7" />
          </svg>
        </div>
        <p className={styles.successEyebrow}>УСПЕШНО ОТПРАВЛЕНО</p>
        <h2 className={styles.successTitle}>
          {isEdit ? 'Лот отправлен на повторную модерацию' : 'Аукцион на модерации'}
        </h2>
        <p className={styles.successBody}>
          Обычно проверка занимает до часа. Мы пришлём уведомление в Telegram,
          когда лот опубликуют.
        </p>
        <div className={styles.successActions}>
          <Link to={`/auction/${submitted}`}>
            <Button variant="primary" fullWidth>Открыть лот</Button>
          </Link>
          <Link to="/">
            <Button variant="secondary" fullWidth>На главную</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button className={styles.closeBtn} onClick={goBack} aria-label="Назад">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            {step === 1 ? <path d="M18 6 6 18M6 6l12 12" /> : <path d="m15 18-6-6 6-6" />}
          </svg>
        </button>
        <p className={styles.headerTitle}>{isEdit ? 'Редактировать лот' : 'Создать аукцион'}</p>
        <div style={{ width: 36 }} />
      </header>

      {isEdit && auction?.status === 'rejected' && auction.rejected_reason && (
        <div className={styles.rejectBanner}>
          <p className={styles.rejectBannerTitle}>Лот отклонён модератором</p>
          <p className={styles.rejectBannerText}>{auction.rejected_reason}</p>
          <p className={styles.rejectBannerHint}>Исправь и отправь заново.</p>
        </div>
      )}

      <div
        className={styles.stepperWrap}
        style={{ ['--stepper-total' as never]: TOTAL_STEPS }}
      >
        <Stepper current={step} total={TOTAL_STEPS} labels={STEP_LABELS} />
      </div>

      <div className={styles.content}>
        {step === 1 && <Step1Photos />}
        {step === 2 && <Step2Basic />}
        {step === 3 && <Step3Pricing />}
        {step === 4 && <Step4Tags />}
        {step === 5 && <Step5Preview />}
      </div>

      {error && (
        <div className={styles.errorBar}>
          {error}
        </div>
      )}

      <div className={styles.actions}>
        {step < TOTAL_STEPS ? (
          <Button variant="primary" size="lg" fullWidth onClick={goNext}>
            Далее
          </Button>
        ) : (
          <Button
            variant="primary"
            size="lg"
            fullWidth
            loading={isPending}
            onClick={handleSubmit}
          >
            {isEdit ? 'Отправить заново' : 'Отправить на модерацию'}
          </Button>
        )}
      </div>
    </div>
  );
};